import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRounded from "@mui/icons-material/ImageRounded";

import { t } from "@i18n";

/**
 * Preset file kinds for {@link FileDropAssetField} (accept + copy + chip).
 * @enum {string}
 */
export const AssetType = Object.freeze({
  PDF: "pdf",
  IMAGE: "image",
});

function defaultFormatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[i]}`;
}

/**
 * UI + accept metadata per {@link AssetType}. The copy fields hold i18n keys
 * (namespace-prefixed) rather than literals, so they resolve at render time and
 * follow a language switch — never call `t()` while this module is evaluated.
 * @type {Record<string, {
 *   accept: string,
 *   dragLine: string,
 *   hintPrefix: string,
 *   statusChipText: string,
 *   StatusChipIcon: import('react').ElementType,
 * }>}
 */
export const ASSET_TYPE_CONFIG = {
  [AssetType.PDF]: {
    accept: "application/pdf,.pdf",
    dragLine: "content:assetTypes.pdf.dragLine",
    hintPrefix: "content:assetTypes.pdf.hint",
    statusChipText: "content:assetTypes.pdf.chip",
    StatusChipIcon: PictureAsPdfRoundedIcon,
  },
  [AssetType.IMAGE]: {
    accept:
      "image/png,image/jpeg,image/jpg,image/heic,image/heif,.png,.jpg,.jpeg,.heic,.heif",
    dragLine: "content:assetTypes.image.dragLine",
    hintPrefix: "content:assetTypes.image.hint",
    statusChipText: "content:assetTypes.image.chip",
    StatusChipIcon: ImageRounded,
  },
};

/**
 * @param {string} assetType
 * @returns {string}
 */
export function getAssetAccept(assetType) {
  const cfg = ASSET_TYPE_CONFIG[assetType];
  return cfg ? cfg.accept : ASSET_TYPE_CONFIG[AssetType.PDF].accept;
}

/**
 * @param {string} assetType
 * @param {number} maxBytes
 * @param {(n: number) => string} [formatBytes]
 * @returns {string}
 */
export function buildAssetHint(assetType, maxBytes, formatBytes = defaultFormatBytes) {
  const cfg = ASSET_TYPE_CONFIG[assetType] ?? ASSET_TYPE_CONFIG[AssetType.PDF];
  return t("content:assetTypes.hintTemplate", {
    prefix: t(cfg.hintPrefix),
    size: formatBytes(maxBytes),
  });
}

const IMAGE_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/heic",
  "image/heif",
]);

const IMAGE_EXT = [".png", ".jpg", ".jpeg", ".heic", ".heif"];

function isPdfFile(file) {
  if (!file) return false;
  if (file.type === "application/pdf") return true;
  const name = file.name || "";
  return typeof name === "string" && name.toLowerCase().endsWith(".pdf");
}

function isImageFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type && IMAGE_MIME.has(type)) return true;
  const name = (file.name || "").toLowerCase();
  return IMAGE_EXT.some((ext) => name.endsWith(ext));
}

/**
 * @param {File} file
 * @param {string} assetType
 * @param {number} maxBytes
 * @param {(n: number) => string} [formatBytes]
 * @returns {{ valid: true } | { valid: false, message: string }}
 */
export function validateAssetFile(file, assetType, maxBytes, formatBytes = defaultFormatBytes) {
  if (!file) {
    return { valid: false, message: t("content:upload.noFileSelected") };
  }
  if (file.size > maxBytes) {
    return {
      valid: false,
      message: t("content:upload.tooLarge", { size: formatBytes(maxBytes) }),
    };
  }
  const at = assetType ?? AssetType.PDF;
  if (at === AssetType.PDF) {
    if (!isPdfFile(file)) {
      return { valid: false, message: t("content:upload.onlyPdf") };
    }
  } else if (at === AssetType.IMAGE) {
    if (!isImageFile(file)) {
      return {
        valid: false,
        message: t("content:upload.onlyImages"),
      };
    }
  }
  return { valid: true };
}
